import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { type InputDropdownOption, SelectInput, SwitchInputGroup } from '@interxfi/design-system/components/input';
import type { IxcRegistrationV1Employment } from '@interxfi/types/generated/api';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import {
  defaultEmployedFormValues,
  EmploymentEmployedForm,
  employedFormSchema,
  formValuesToEmployed,
  getInitialFormValuesForEmployed,
} from './employment-employed-form';
import { defaultHomemakerFormValues, homemakerFormSchema } from './employment-homemaker-form';
import {
  defaultPartTimeFormValues,
  EmploymentPartTimeForm,
  formValuesToPartTime,
  getInitialFormValuesForPartTime,
  partTimeFormSchema,
} from './employment-part-time-form';
import { defaultRetiredFormValues, retiredFormSchema } from './employment-retired-form';
import {
  defaultSeasonalFormValues,
  EmploymentSeasonalForm,
  formValuesToSeasonal,
  getInitialFormValuesForSeasonal,
  seasonalFormSchema,
} from './employment-seasonal-form';
import {
  defaultSelfEmployedFormValues,
  EmploymentSelfEmployedForm,
  formValuesToSelfEmployed,
  getInitialFormValuesForSelfEmployed,
  selfEmployedFormSchema,
} from './employment-self-employed-form';
import { defaultStudentFormValues, studentFormSchema } from './employment-student-form';
import { defaultUnemployedFormValues, unemployedFormSchema } from './employment-unemployed-form';

export const employmentFormSchema = z.object({
  employeeOfCorrespondent: z.boolean(),
  type: z.discriminatedUnion('kind', [
    employedFormSchema,
    selfEmployedFormSchema,
    partTimeFormSchema,
    seasonalFormSchema,
    unemployedFormSchema,
    retiredFormSchema,
    studentFormSchema,
    homemakerFormSchema,
  ]),
});

export type EmploymentFormValues = z.infer<typeof employmentFormSchema>;

const kindOptions = [
  {
    value: 'employed',
    label: 'Full-Time',
  },
  {
    value: 'selfEmployed',
    label: 'Self-Employed',
  },
  {
    value: 'partTime',
    label: 'Part-Time',
  },
  {
    value: 'seasonal',
    label: 'Seasonal',
  },
  {
    value: 'unemployed',
    label: 'Unemployed',
  },
  {
    value: 'retired',
    label: 'Retired',
  },
  {
    value: 'student',
    label: 'Student',
  },
  {
    value: 'homemaker',
    label: 'Homemaker',
  },
] as const satisfies InputDropdownOption<EmploymentFormValues['type']['kind']>[];

export const defaultEmploymentFormValues: EmploymentFormValues = {
  employeeOfCorrespondent: false,
  type: { kind: '' } as unknown as EmploymentFormValues['type'], // because kind is required, we'll default to an empty string to force a selection
};

export function formValuesToEmployment(values: EmploymentFormValues): IxcRegistrationV1Employment {
  return match(values)
    .with({ type: { kind: 'employed' } }, (v) => ({
      employed: formValuesToEmployed(v.type),
    }))
    .with({ type: { kind: 'selfEmployed' } }, (v) => ({
      selfEmployed: formValuesToSelfEmployed(v.type),
    }))
    .with({ type: { kind: 'partTime' } }, (v) => ({
      partTime: formValuesToPartTime(v.type),
    }))
    .with({ type: { kind: 'seasonal' } }, (v) => ({
      seasonal: formValuesToSeasonal(v.type),
    }))
    .with({ type: { kind: 'unemployed' } }, () => ({
      unemployed: {},
    }))
    .with({ type: { kind: 'retired' } }, () => ({
      retired: {},
    }))
    .with({ type: { kind: 'student' } }, () => ({
      student: {},
    }))
    .with({ type: { kind: 'homemaker' } }, () => ({
      homemaker: {},
    }))
    .exhaustive();
}

export function getInitialFormValuesForEmployment(
  employment: IxcRegistrationV1Employment | undefined,
  isEmployeeOfCorrespondent: boolean | undefined,
): EmploymentFormValues {
  if (!employment) {
    return defaultEmploymentFormValues;
  }

  return {
    employeeOfCorrespondent: Boolean(isEmployeeOfCorrespondent),
    type: match(employment)
      .returnType<EmploymentFormValues['type']>()
      .with({ employed: P.not(P.nullish) }, (e) => getInitialFormValuesForEmployed(e.employed))
      .with({ selfEmployed: P.not(P.nullish) }, (e) => getInitialFormValuesForSelfEmployed(e.selfEmployed))
      .with({ partTime: P.not(P.nullish) }, (e) => getInitialFormValuesForPartTime(e.partTime))
      .with({ seasonal: P.not(P.nullish) }, (e) => getInitialFormValuesForSeasonal(e.seasonal))
      .with({ unemployed: P.not(P.nullish) }, () => defaultUnemployedFormValues)
      .with({ retired: P.not(P.nullish) }, () => defaultRetiredFormValues)
      .with({ student: P.not(P.nullish) }, () => defaultStudentFormValues)
      .with({ homemaker: P.not(P.nullish) }, () => defaultHomemakerFormValues)
      .exhaustive(() => defaultEmploymentFormValues.type),
  } as const satisfies EmploymentFormValues;
}

export interface EmploymentFormProps {
  lens: Lens<EmploymentFormValues>;
}

export function EmploymentForm({ lens }: EmploymentFormProps) {
  const { setValue } = useFormContext();
  const employmentTypeLens = lens.focus('type');
  const { name: employmentTypeName } = employmentTypeLens.interop();
  const employmentKindInterop = employmentTypeLens.focus('kind').interop();
  const kindSelection = useWatch({ ...employmentKindInterop, defaultValue: '' as EmploymentFormValues['type']['kind'] });

  const handleKindChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newKind = e.target.value as EmploymentFormValues['type']['kind'];

      setValue(
        employmentTypeName,
        match(newKind)
          .with('employed', () => defaultEmployedFormValues)
          .with('selfEmployed', () => defaultSelfEmployedFormValues)
          .with('partTime', () => defaultPartTimeFormValues)
          .with('seasonal', () => defaultSeasonalFormValues)
          .with('unemployed', () => defaultUnemployedFormValues)
          .with('retired', () => defaultRetiredFormValues)
          .with('student', () => defaultStudentFormValues)
          .with('homemaker', () => defaultHomemakerFormValues)
          .exhaustive(() => defaultEmploymentFormValues.type),
        { shouldValidate: true },
      );
    },
    [setValue, employmentTypeName],
  );

  return (
    <VerticalStack gap={4}>
      <SelectInput label="Type" name={employmentKindInterop.name} onChange={handleKindChange} options={kindOptions} required value={kindSelection} />

      {kindSelection === 'employed' ? <EmploymentEmployedForm lens={employmentTypeLens.narrow()} /> : null}
      {kindSelection === 'selfEmployed' ? <EmploymentSelfEmployedForm lens={employmentTypeLens.narrow()} /> : null}
      {kindSelection === 'partTime' ? <EmploymentPartTimeForm lens={employmentTypeLens.narrow()} /> : null}
      {kindSelection === 'seasonal' ? <EmploymentSeasonalForm lens={employmentTypeLens.narrow()} /> : null}

      <SwitchInputGroup>
        <AppSwitchInputWithLens
          label="This individual or a member of their household is employed by the correspondent."
          lens={lens.focus('employeeOfCorrespondent')}
        />
      </SwitchInputGroup>
    </VerticalStack>
  );
}
